/// <amd-module name="VSS/Platform/Search" />
import React = require("react");
import { IObservable, ObservableValue } from "VSS/Core/Observable";
import { IVssObservableService, IVssService, Services, VssObservableService } from "./Context";
import { IContributedService, IVssContributionService } from "./Contribution";
import { IVssNavigationService } from "./Navigation";

/**
 * When the provider is interacting with the search input UI it is given an instance of the
 * ISearchInput interface. This allows the provider to read or make changes to the current
 * input value on demand.
 */
export interface ISearchInput extends IObservable<string> {
    /**
     * appendText allows the caller to add text to the current input value.
     * optional paramater triggerChange, to disable triggering of change
     */
    appendText: (text: string, triggerChange?: boolean) => void;

    /**
     * queryText allows the caller to retrieve the current input value.
     */
    queryText: () => string;

    /**
     * allows the consumer to replace the current text in the input with the one supplied.
     * optional paramater triggerChange, to disable triggering of change
     */
    replaceText: (text: string, triggerChange?: boolean) => void;

    /**
     * allows the consumer to set the visibility of the help component.
     */
    setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void;

    /**
     * allows the consumer to set the Search Provider for the help items
     * If set, the input control will execute search on this provider rather than
     * the pivot provider
     */
    setHelpSearchProviderId: (helpSearchProviderId: string) => void;

    /**
     * Optional: allows the consumer to execute search results and land on search results page.
     */
    executeSearch?: (openInNewWindow?: boolean) => void;

    /**
     * Optional: Callback to execute when search box prefix pill is removed.
     */
    onSearchPrefixRemove?: () => void;

    /**
     * Optional: allows the consumer to set placeholder text in the search box.
     * If set to undefined, it will show default search placeholder.
     */
    setPlaceholder?: (placeholder?: string) => void;

    /**
     * Optional: allows the consumer to set props to show prefix pill in the search box.
     * It can be removed by pressing backspace when empty text, clicking clear 'X' icon or
     * by setting this props to undefined.
     */
    setSearchPrefixProps?: (searchPrefixProps?: ISearchPrefixProps) => void;
}

/**
 * The PagingDirection is used to control which set of matches is returned based
 * on the query. When continuation tokens are used the direction will be relative
 * to the token given.
 *
 * When a query is first started First/Forward will return the first page.
 *  Previous is ignored, and Last should return the last page if possible.
 */
export const enum PagingDirection {
    /**
     * Return the next page for this query.
     */
    Next = 0,

    /**
     * Return the previous page for this query.
     */
    Previous = 1,

    /**
     * Return the first page for this query.
     */
    First = 2,

    /**
     * Return the last page for this query.
     */
    Last = 3
}

/**
 * Props for search prefix pill
 */
export interface ISearchPrefixProps {
    /**
     * Text to show in the prefix pill. 
     */
    prefixText: string;

    /**
     * Name of the icon to be shown in the prefix pill.
     */
    iconName?: string;
}

/**
 * The search queries defines the inputs used to perform the query.
 */
export interface ISearchQuery {
    /**
     * The providerId that the query is targetting.
     */
    providerId: string;

    /**
     * Basic text being used in the query.
     */
    queryText: string;

    /**
     * openInNewWindow can be specified to control how the search is executed.
     * When openInNewWindow is set to true, the search should be shown in a new window
     * if possible. Some coordinators MAY choose to ignore this request.
     */
    openInNewWindow?: boolean;

    /**
     * pagingDirection is used to control how the set of results is determined based
     * on the current query. The default value is PagingDirection.Next.
     */
    pageDirection?: PagingDirection;

    /**
     * The pageSize value defines how many results the caller would like to receive.
     * If no pageSize or the value 0 is defined the search provider should return the
     * default size for the query.
     */
    pageSize?: number;

    /**
     * A continuation token is used to define a new query based on the results from a
     * previously executed query. This is usually used in paging operations.
     */
    continuationToken?: string;
}

/**
 * For each match that is found, their is a set of values the provider MAY return to
 * describe the match.
 */
export interface ISearchMatch {
    /**
     * Link to the document that contains the matched result.
     */
    href: string;

    /**
     * String that should be displayed that shows the best value for the match. This text
     * MAY be linked to the document supplied in the href.
     */
    matchText: string;

    /**
     * An optional string that MAY be used in the UI to give further details about the match
     * that the matchText gives. This may include things like document details, date times,
     * versions, etc.
     */
    matchDetails?: string;

    /**
     * Relevance is an optional value used to describe how well the value matched the query.
     */
    relevance?: number;

    /**
     * The render method is used to retrieve the default presentation of this matched
     * result. Consumers MAY use this to get a fully detailed UI for this match.
     */
    render(): JSX.Element;
}

/**
 * A system that wishes to participate in the global search integration and contains data
 * that can be searched needs to implement an ISearchProvider and register it with the
 * SearchProviders. This enables the provider to be called when the caller initiates a search.
 *
 * The search provider should expose a number of display properties that allow the general
 * search UI to interact with the provider. These are things like display names, help UI,
 * and other helpful elements that are used to guide the user.
 *
 * The page interacts with the search provider in 1 of 2 ways. The general facilities execute
 * the query against each provider the caller is interested in. This may be all providers or
 * a specific subset.
 *
 *  1) The caller uses the render function and is returned a pre-rendered form of the results
 *      from each of the providers called. This will produce a consolidated UI that has all
 *      results.
 *
 *  2) The calles uses the execute function and is returned a set of search matches from each
 *      provider. A search match has both high level details about the match along a function
 *      that can be used to convert the match into its default presentation.
 */
export interface ISearchProvider extends IVssService {
    /**
     * Execute is used to retrieve a raw set of matches from the provider. These matches may
     * be rendered as a result using the builtin render method which may add specific UI
     * that is not available through custom rendering.
     *
     * @param searchQuery This defines the inputs for the search being performed.
     */
    execute: (searchQuery: ISearchQuery) => ISearchStatus;

    /**
     * render is used to convert a result that was generated from this provider into a UI
     * this provider offers. The caller isn't required to use the built-in rendering function
     * but may get provider specific UI that is not available from a match or custom rendering.
     *
     * @param searchStatus This is the status of a pending search query. If no parameter is
     *  passed it denotes that no query has been run and the provider should render guidance
     *  on queries for this provider.
     */
    render?: (searchStatus?: ISearchStatus) => JSX.Element;

    /**
     * A given provider may implement a default query transformation. This allows the
     * searchProvider to add basic information to the query. This allows the provider to
     * add things that are used at query execution time that are sensitive to the provider.
     */
    getQuery?: (searchQuery: ISearchQuery) => ISearchQuery;

    /**
     * The help API is used to show UI about special features the provider offers. This will
     * usually be shown in a small factor UI show this should be condensed information and if
     * the provider has a number of features, they MAY offer a link to a details page that
     * can be used to explain the search options.
     */
    help?: (searchInput: ISearchInput) => JSX.Element | null;

    /**
     * A given provider may perform an action before doing a search using the searchQuery.
     * For example wit search provider implements performAction to open a valid work item in a modal dialog.
     * If an action is performed i.e. returns true, search isn't performed against that searchQuery.
     */
    performAction?: (searchQuery: ISearchQuery) => Promise<boolean>;
}

/**
 * ContributedSearchCoordinators are contributed services that are used to execute page level
 * search queries.
 *
 * Type: "ms.vss-web.search-coordinator"
 */
export interface IContributedSearchCoordinator extends IContributedService {
    /**
     * The prioity of this coordinator. The coordinator with the highest priority will
     * be used by the search service to perform searches.
     */
    priority: number;
}

/**
 * The IContributedSearchProvider interface declares the set of information provided to the
 * search service via a contribution. This ties a provider to a service implementation along
 * with some basic display information.
 *
 * Type: "ms.vss-web.search-provider"
 */
export interface IContributedSearchProvider extends IContributedService {
    /**
     * Display name of the Search Provider.
     *
     * Examples: Code, Work Items, Builds, Projects, etc.
     */
    providerName: string;

    /**
     * The placeholder is an optional string that can be used in the UI when your describing
     * to the user what they are searching for.
     */
    placeholder?: string;

    /**
     * The types of documents this search provider searches.
     */
    supportedDocTypes?: string[];

    /**
     * The priority of this provider. The provider with the highest priority will
     * be used when no selected provider is available for the current page navigation levels
     */
    priority?: number;
}

/**
 * An ISearchResult encapsulates the results of a completed search query.
 */
export interface ISearchResult {
    /**
     * If the query returns a continuation token it should be supplied back to query for
     * this provider when the caller wants results from this query, ie they want the next
     * or previous page, etc.
     */
    continuationToken?: string;

    /**
     * If a query fails to complete normally the exception value will have the
     * information about the failure.
     */
    exception?: any;

    /**
     * This is the results returned from the provider. If matches is undefined
     * or an empty array, no matches were found for the query.
     */
    matches?: ISearchMatch[];
}

/**
 * An ISearchStatus represents the status of a single ISearchProvider search being executed.
 */
export interface ISearchStatus {
    /**
     * The initial search query used to execute this query.
     */
    searchQuery: ISearchQuery;

    /**
     * Once the search has completed the status is updated to contain the result from
     * the provider.
     */
    searchResult: ObservableValue<ISearchResult | undefined>;
}

/**
 * The ISearchCoordinator interface is designed to allow differnt implementations of
 * searching based on the context of the page. SearchCoordinators are registered with
 * the search sub-system and have a priority. The highest priority coordinator on the
 * page is the one used to perform searches.
 */
export interface ISearchCoordinator extends IVssService {
    /**
     * The execute method is used to execute and coordinate the searches. The returned
     * search result can be used by the caller to monitor the results. They can also
     * request the status of any provider that was requested.
     *
     * @param searchQuery This defines the inputs for the search being performed.
     */
    execute: (searchQuery: ISearchQuery) => Promise<ISearchStatus>;
}

/**
 * The IVssSearchService can be used to execute and coordinate the results of a search
 * operation.
 *
 * Events:
 *  searchStarted - ISearchStatus
 *  providerSelected - string (providerId)
 */
export interface IVssSearchService extends IVssObservableService<ISearchStatus | string> {
    /**
     * The execute method is used to execute and coordinate the searches. The returned
     * search result can be used by the caller to monitor the results. They can also
     * request the status of any provider that was requested.
     *
     * @param searchQuery This defines the inputs for the search being performed.
     *
     */
    execute: (searchQuery: ISearchQuery) => Promise<ISearchStatus>;

    /**
     * getProviders is used to determine the set of search providers that are available
     * for use in performing searches.
     *
     * @returns The set of available search providers.
     */
    getProviders: () => { [providerId: string]: IContributedSearchProvider };

    /**
     * getDefaultProvider is used to determine which provider is the currently selected.
     * This may be set by a call to setSelectedProvider, or it may be determined through
     * hints within the page that help select the best provider for the situation.
     *
     * @returns The currently selected search providerId, if no providers are available,
     *  it returns undefined.
     */
    getSelectedProvider: () => string | undefined;

    /**
     * setSelectedProvider is used to control which provider should be used in the UI as
     * the selected provider. Others can listen to the providerSelected event to be
     * notified when the provider is changed.
     */
    setSelectedProvider: (providerId: string) => void;
}

/**
 * Well-known contributionTypes used for search management
 */
const SearchCoordinatorType = "ms.vss-web.search-coordinator";
const SearchProviderType = "ms.vss-web.search-provider";

class VssSearchService extends VssObservableService<ISearchStatus | string> implements IVssObservableService<ISearchStatus | string> {
    private selectedProviderId?: string;
    private defaultProviderId?: string;

    public execute(searchQuery: ISearchQuery): Promise<ISearchStatus> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        // Get the set of search coordinators that are configured for this page.
        const coordinators = contributionService.getContributionsByType<IContributedSearchCoordinator>(SearchCoordinatorType);

        // Determine the highest priority coordinator.
        let highestPriorityId: string | undefined = undefined;
        let highestPriority: IContributedSearchCoordinator | undefined = undefined;
        for (const coordinatorId in coordinators) {
            if (!highestPriority || coordinators[coordinatorId].priority > highestPriority.priority) {
                highestPriorityId = coordinatorId;
                highestPriority = coordinators[highestPriorityId];
            }
        }

        if (highestPriorityId) {
            return contributionService.getService<ISearchCoordinator>(highestPriorityId).then((searchCoordinator: ISearchCoordinator | undefined) => {
                if (searchCoordinator) {
                    return searchCoordinator.execute(searchQuery).then(searchStatus => {
                        this._notify(searchStatus, "searchStarted");
                        return searchStatus;
                    });
                }

                throw new Error("Failed to create the configured search coordinator");
            });
        }

        return Promise.reject("No search coordinator is available to perform the search operation.");
    }

    public getProviders(): { [providerId: string]: IContributedSearchProvider } {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getContributionsByType<IContributedSearchProvider>(SearchProviderType);
    }

    public getSelectedProvider(): string | undefined {
        let selectedProviderId: string | undefined = this.selectedProviderId || this.defaultProviderId;

        if (!selectedProviderId) {
            const providers = this.getProviders();
            const providerKeys = Object.keys(providers);

            if (providerKeys.length > 0) {
                const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
                const displayedNavigation = navigationService.getDisplayedNavigation();

                // Build the displayed document types from the list of displayedDocTypes from each navigation
                // starting at the last and working back to the root.
                const displayedDocTypes: string[] = [];
                for (let index = displayedNavigation.length - 1; index >= 0; index--) {
                    const contributedNavigation = navigationService.getNavigation(displayedNavigation[index]);
                    if (contributedNavigation && contributedNavigation.displayedDocTypes) {
                        displayedDocTypes.splice(displayedNavigation.length, 0, ...contributedNavigation.displayedDocTypes);
                    }
                }

                // Loop through the displayed document types and determine if we have a provider
                // that supports this document type, if so use it.
                determineProvider: for (const docType of displayedDocTypes) {
                    for (const providerId in providers) {
                        const provider = providers[providerId];
                        if (provider.supportedDocTypes && provider.supportedDocTypes.indexOf(docType) !== -1) {
                            selectedProviderId = providerId;
                            break determineProvider;
                        }
                    }
                }

                // If no provider was found, compare the priorities of the provider and return the provider with higest priority
                // else if no priority exists return the the first provider
                if (!selectedProviderId) {
                    selectedProviderId = providerKeys[0];
                    for (const providerId in providers) {
                        const provider = providers[providerId];
                        const selectedProvider = providers[selectedProviderId];
                        if (
                            typeof provider.priority !== "undefined" &&
                            (typeof selectedProvider.priority === "undefined" || provider.priority > selectedProvider.priority)
                        ) {
                            selectedProviderId = providerId;
                        }
                    }
                }

                this.defaultProviderId = selectedProviderId;
            }
        }

        return selectedProviderId;
    }

    public setSelectedProvider(providerId: string): void {
        const availableProviders = this.getProviders();
        if (availableProviders[providerId]) {
            this.selectedProviderId = providerId;
            this._notify(this.selectedProviderId, "providerSelected");
        }
    }
}

Services.add("IVssSearchService", { serviceFactory: VssSearchService });
